import { Check } from "lucide-react";
import { Container } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { pricingPlans, signupUrl, storageAddon } from "@/lib/content";

export function PricingModel() {
  return (
    <section id="pricing" aria-labelledby="pricing-heading" className="py-16 sm:py-24">
      <Container>
        <SectionHeading
          titleId="pricing-heading"
          eyebrow="Launch pricing"
          title="Start free. Upgrade when your privacy workload grows."
          description="Pricing is per workspace and published upfront. Paid checkout is coming later; these are the locked launch prices and attachment allowances."
        />

        <div className="mx-auto mt-12 grid max-w-6xl gap-6 lg:grid-cols-3">
          {pricingPlans.map((plan) => {
            const isFree = plan.name === "Free";
            return (
              <Card
                key={plan.name}
                className={isFree ? "flex h-full min-w-0 flex-col" : "flex h-full min-w-0 flex-col border-primary/30 shadow-elevated"}
              >
                <CardHeader>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <Badge variant={isFree ? "outline" : "brand"}>{plan.badge}</Badge>
                  </div>
                  <div className="pt-2">
                    <p className="text-3xl font-bold tracking-tight text-foreground">{plan.price}</p>
                    <p className="mt-1 text-sm font-medium text-muted-foreground">{plan.annual}</p>
                  </div>
                  <CardDescription>{plan.tagline}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col pt-0">
                  <div className="mb-5 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground">
                    {plan.storage}
                  </div>
                  <ul className="flex-1 space-y-3">
                    {plan.points.map((point) => (
                      <li key={point} className="flex gap-2.5 text-sm leading-relaxed text-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>

                  {isFree ? (
                    <Button className="mt-6" asChild>
                      <a href={signupUrl}>Start Free</a>
                    </Button>
                  ) : (
                    <Button className="mt-6" variant="outline" disabled>
                      Paid checkout coming soon
                    </Button>
                  )}
                  <p className="mt-2 text-center text-xs text-muted-foreground">{plan.availability}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mx-auto mt-8 max-w-4xl rounded-xl border border-border bg-secondary/30 p-5 text-center sm:p-6">
          <p className="text-base font-semibold text-foreground">
            Need more attachment storage? {storageAddon.amount} for {storageAddon.price} per block.
          </p>
          <p className="mx-auto mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {storageAddon.copy}
          </p>
        </div>

        <p className="mx-auto mt-6 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground">
          Exact Pro and Business record limits and monthly document-generation allowances are intentionally not shown until those numeric entitlements are separately locked. Freemius is planned as Merchant of Record for paid subscriptions.
        </p>
      </Container>
    </section>
  );
}
